import { ProductGroupTypes } from '@/enums/ProductGroupTypes';

export const ajaxUrls = {
    search: {
        query(
            productGroup: string,
            keyword = '',
            codes?: string,
            narrowKeyword?: string,
        ) {
            let productGroupFilter = '';
            switch (productGroup) {
                case ProductGroupTypes.Trademark:
                    productGroupFilter = 'filter[isTrademark]=1&';
                    break;
                case ProductGroupTypes.CustomerRelations:
                    productGroupFilter = 'filter[isCustomerRelation]=1&';
                    break;
                case ProductGroupTypes.Goodwill:
                    productGroupFilter = 'filter[isGoodwill]=1&';
                    break;
                case ProductGroupTypes.Technology:
                    productGroupFilter = 'filter[isTechnology]=1&';
                    break;
                case ProductGroupTypes.Software:
                    productGroupFilter = 'filter[isSoftware]=1&';
                    break;
                default:
                    productGroupFilter = '';
            }
            return `brands/search?${productGroupFilter}filter[keywords]=${keyword}&page[size]=30000${''}&include=brandCountry,valuationCause,revenueType,cpcs${
                codes ? '&filter[cpcCodes]=' + codes : ''
            }${
                narrowKeyword
                    ? '&filter[narrowDownKeywords]=' + narrowKeyword
                    : ''
            }`;
        },
        cpcCodes(prodGroup: string, id?: number) {
            if (id) {
                return `cpcs/search/${id}?filter[prodGroup]=${prodGroup}`;
            }
            return `cpcs/search?filter[prodGroup]=${prodGroup}`;
        },
        savedSearchesWithGroup(prodGroup: string) {
            return `saved-searches?filter[prodGroup]=${prodGroup}`;
        },
        savedSearches: 'saved-searches',
        savedSearch(id: number) {
            return `saved-searches/${id}`;
        },
        savedSearchDownload(id: number) {
            return `saved-searches/${id}/download`;
        },
        productTotalItems() {
            return `brands/count`;
        },
    },
    user: {
        usersShoppingListTotalItems() {
            return `shoplist/items/count`;
        },
        usersShoppingListRecordsWithGroup(productGroup: ProductGroupTypes, isCount = false) {
            return isCount
                ? ('shoplist/items/count?filter[prodGroup]=' + productGroup)
                : ('shoplist/items?filter[prodGroup]='
                    + productGroup
                    + '&include=brand.brandCountry,brand.revenueType,brand.valuationCause,brand.cpcs'
            );
        },
        shoppingListWithId(itemId: number) {
            return `shoplist/items/${itemId}`;
        },
        usersShoppingList: 'shoplist/items',
        shoppingListFilterOptions: 'user/filter-options',
        shoppingCart: 'user/shopping-cart',
        usersShoppingCartTotalItems() {
            return `shopcart/items/count`;
        },
        usersShoppingCartRecordsWithGroup(productGroup: ProductGroupTypes, isCount = false) {
            return isCount
                ? ('shopcart/items/count?filter[prodGroup]=' + productGroup)
                : ('shopcart/items?filter[prodGroup]='
                    + productGroup
                    + '&include=brand.brandCountry,brand.revenueType,brand.valuationCause,brand.cpcs'
                );
        },
        shoppingCartWithId(itemId: number) {
            return `shopcart/items/${itemId}`;
        },
        usersShoppingCart: 'shopcart/items',
        register: 'auth/register',
        confirmToken: 'auth/confirm',
        confirmDevice: 'auth/confirm-device',
        refreshToken: 'auth/refresh-token',
        changePassword: 'passwords/reset',
        login: 'auth/login',
        resetPassword: 'passwords/request-reset',
        updatePassword: 'passwords/update',
        userData(email: string) {
            return `users/${email}?include=profilePicture,country`;
        },
        profilePicture: 'users/profile-picture',
        countries: 'countries?sort=name&page[size]=1000',
        brands(productGroup: string) {
            return `brands?prodGroup=${productGroup}`;
        },
        lastSignIns: 'last-sign-ins',
        billingAddresses: 'billing-addresses?include=country',
        billingAddressesWithId(id: number) {
            return `billing-addresses/${id}`;
        },
        requestEmailReset: 'auth/request-email-reset',
        invoices: 'invoices?include=invoiceItem',
        invoiceDownload(invoiceId: number) {
            return `invoices/${invoiceId}/download`;
        },
        projectBillingDownload(projectBillingId: number) {
            return `project/billings/${projectBillingId}/download`;
        },
        brandDownload(invoiceId: number) {
            return `invoices/${invoiceId}/download`;
        },
        shopcartSummary(type: string) {
            return `shopcart-summary/${type}`;
        },
        orderSummary(type: string) {
            return `order-summary/${type}`;
        },
        contracts(typeName: string) {
            return `contracts?filter[contractType.name]=${encodeURI(
                `%${typeName}%`,
            )}`;
        },
        buyUsingSubscriptions: 'shopcart-checkout/use-subscription',
        buyUsingCredits: 'shopcart-checkout/use-prepaid-credit',
        buyUsingPayPerView: 'shopcart-checkout/use-pay-per-view',
        buySubscription: 'contract-checkout/buy-subscription',
        buyCredits: 'contract-checkout/buy-prepaid-credit',
        projectBillings:
            'project/billings?include=contract,contract.contractType,contractItems.brand,project&filter[contractType]=subscription,prepaid,multi-seat subscription',
        projectBillingsSubscription:
            'project/billings?filter[contractType]=subscription&include=contractItems.brand',
        projectBillingsCredits:
            'project/billings?filter[contractType]=prepaid&include=contractItems.brand',
        bluesnapToken: 'payment/bluesnap/token',
    },
    projects: {
        projectsList: 'projects?page[size]=1000&page[number]=1',
        projectsListSorted: 'projects?page[size]=1000&page[number]=1&sort=-createdAt',
        projectsListWithGroup(productGroup: ProductGroupTypes) {
            return 'projects?page[size]=1000&page[number]=1&sort=-createdAt&filter[prodGroup]=' + productGroup;
        },
        existingProjectsWithGroup(productGroup: ProductGroupTypes) {
            return `projects?page[size]=1000&page[number]=1&sort=-createdAt&filter[prodGroup]=${productGroup}&filter[isMain]=0`;
        },
        singleProject(projectId: number) {
            return `projects/${projectId}?include=projectItems.brand.valuationCause,projectItems.brand.brandCountry`;
        },
        projectItemWithId(itemId: number) {
            return `project/items/${itemId}`;
        },
        singleProjectDetails(itemId: number) {
            return (
                'project/items/' +
                itemId +
                '?include=' +
                'brand.valuerCountry,' +
                'brand.valuationCause,' +
                'brand.currency,' +
                'brand.concept,' +
                'brand.brandCountry,' +
                'brand.territory,' +
                'brand.revenueType,' +
                'brand.profitType,projects'
            );
        },
        projectItem: 'project/items?include=brand',
        contractItem(productGroup: string) {
            return `contract/items?include=brand&filter[prodGroup]=${productGroup}`;
        },
        statisticalAnalysis(ids: string, productGroup: string) {
            if (productGroup === ProductGroupTypes.Trademark) {
                return `statistic/${ids}?include=valuerCountry,brandCountry&filter[isTrademark]=1&page[size]=1000`;
            }
            if (productGroup === ProductGroupTypes.CustomerRelations) {
                return `statistic/${ids}?include=valuerCountry,brandCountry&filter[isCustomerRelation]=1
                &page[size]=1000`;
            }
            if (productGroup === ProductGroupTypes.Goodwill) {
                return `statistic/${ids}?include=valuerCountry,brandCountry&filter[isGoodwill]=1
                &page[size]=1000`;
            }
            if (productGroup === ProductGroupTypes.Technology) {
                return `statistic/${ids}?include=valuerCountry,brandCountry&filter[isTechnology]=1
                &page[size]=1000`;
            }
            return `statistic/${ids}?include=valuerCountry,brandCountry&filter[isSoftware]=1&page[size]=1000`;
        },
        statisticalAnalysisDownloadPDF(
            ids: string,
            productGroup: string,
            projectName: string,
            revenueForecastType: string,
        ) {
            if (productGroup === ProductGroupTypes.Trademark) {
                return `statistic/pdf/${ids}?filter[isTrademark]=1&projectName=${projectName}`;
            }
            if (productGroup === ProductGroupTypes.CustomerRelations) {
                return `statistic/pdf/${ids}?filter[isCustomerRelation]=1&projectName=${projectName}`;
            }
            if (productGroup === ProductGroupTypes.Goodwill) {
                return `statistic/pdf/${ids}?filter[isGoodwill]=1&projectName=${projectName}`;
            }
            if (productGroup === ProductGroupTypes.Technology) {
                return `statistic/pdf/${ids}?filter[isTechnology]=1&projectName=${projectName}&revenueForecastType=${revenueForecastType}`;
            }
            return `statistic/pdf/${ids}?filter[isSoftware]=1&projectName=${projectName}&revenueForecastType=${revenueForecastType}`;
        },
        statisticalAnalysisDownloadExcel(
            ids: string,
            productGroup: string,
            projectName: string,
            revenueForecastType: string,
        ) {
            if (productGroup === ProductGroupTypes.Trademark) {
                return `statistic/excel/${ids}?filter[isTrademark]=1&projectName=${projectName}`;
            }
            if (productGroup === ProductGroupTypes.CustomerRelations) {
                return `statistic/excel/${ids}?filter[isCustomerRelation]=1&projectName=${projectName}`;
            }
            if (productGroup === ProductGroupTypes.Goodwill) {
                return `statistic/excel/${ids}?filter[isGoodwill]=1&projectName=${projectName}`;
            }
            if (productGroup === ProductGroupTypes.Technology) {
                return `statistic/excel/${ids}?filter[isTechnology]=1&projectName=${projectName}&revenueForecastType=${revenueForecastType}`;
            }
            return `statistic/excel/${ids}?filter[isSoftware]=1&projectName=${projectName}&revenueForecastType=${revenueForecastType}`;
        },
        brandDownload(ids: string, projectName: string) {
            return `project/items/${ids}/download?projectName=${projectName}`;
        },
        brandMultipleDownload(ids: string, projectName: string) {
            return `project/items/${ids}/download-multiple?projectName=${projectName}`;
        },
        projectOverview(productGroup: string) {
            return `overview?filter[prodGroup]=${productGroup}`;
        },
    },
};
